import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { ArrowLeft, User, ShieldCheck, Star, Play, CheckCircle2, Circle, AlertTriangle, MessageSquare, Video, Mic, FileText, Heart, GitCompareArrows, Calendar } from "lucide-react";
import { brandify } from "../lib/brand";

const EVIDENCE_TONE = {
  CANDIDATE_REPORTED: "bg-slate-100 text-slate-600 border-slate-200",
  RESUME_SUPPORTED: "bg-blue-50 text-blue-700 border-blue-200",
  STAFFHUB_ASSESSED: "bg-amber-50 text-amber-800 border-amber-300",
  STAFFHUB_VERIFIED: "bg-emerald-50 text-emerald-800 border-emerald-300",
};
const EVIDENCE_LABEL = {
  CANDIDATE_REPORTED: "Candidate reported",
  RESUME_SUPPORTED: "Resume supported",
  STAFFHUB_ASSESSED: "ConnectMyHub assessed",
  STAFFHUB_VERIFIED: "ConnectMyHub verified",
};

// Strips a leading duplicate token (e.g., "Senior Senior Software Engineer" -> "Senior Software Engineer")
function sanitizeHeadline(headline) {
  if (!headline) return "";
  return String(headline).replace(/^(\S+)\s+\1\b/i, "$1").trim();
}
const STATUS_TONE = {
  VERIFIED:"bg-emerald-50 text-emerald-800 border-emerald-300",
  ASSESSED:"bg-amber-50 text-amber-800 border-amber-300",
  COMPLETED:"bg-blue-50 text-blue-800 border-blue-200",
  PENDING:"bg-slate-100 text-slate-600 border-slate-200",
  NOT_STARTED:"bg-slate-50 text-slate-400 border-slate-200",
  NOT_VERIFIED:"bg-slate-50 text-slate-500 border-slate-200",
};
const MEDIA_KIND = {
  VIDEO_INTRODUCTION:{label:"Watch Introduction", icon:Video},
  INTERVIEW_AUDIO_RESPONSE:{label:"Listen to Interview Response", icon:Mic},
  SALES_PITCH:{label:"Listen to Sales Pitch", icon:Mic},
  MOCK_CALL:{label:"Listen to Mock Call", icon:Mic},
  ROLE_AUDITION_AUDIO:{label:"Listen to Role Audition", icon:Mic},
  ROLE_AUDITION_VIDEO:{label:"Watch Role Audition", icon:Video},
  WORK_SAMPLE_MEDIA:{label:"View Work Sample", icon:FileText},
};

export default function EmployerCandidate() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const jobOrderId = params.get("job");
  const [c, setC] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [tab, setTab] = useState("overview");
  const [favorite, setFavorite] = useState(false); // UI stub for Phase 3

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${id}/employer-view`, {params: jobOrderId?{job_order_id:jobOrderId}:{}});
      setC(data);
    } catch (e) {
      console.error("employer-view load failed", e);
      toast.error(e.response?.data?.detail || "Load failed");
    }
  }, [id, jobOrderId]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <Layout><div className="text-sm text-slate-500">Loading candidate profile…</div></Layout>;

  const media = c.media || [];
  const groupedMedia = Object.keys(MEDIA_KIND).reduce((acc,k)=>{acc[k]=media.filter(m=>m.media_type===k);return acc;},{});
  const hasIntro = groupedMedia.VIDEO_INTRODUCTION?.length > 0;
  const interviewResponses = groupedMedia.INTERVIEW_AUDIO_RESPONSE || [];
  const auditions = [...(groupedMedia.SALES_PITCH||[]), ...(groupedMedia.MOCK_CALL||[]), ...(groupedMedia.ROLE_AUDITION_AUDIO||[]), ...(groupedMedia.ROLE_AUDITION_VIDEO||[])];
  const workSamples = groupedMedia.WORK_SAMPLE_MEDIA || [];

  const requestInterview = async () => {
    // Look up any shortlist that contains this candidate to obtain company_id/job_order_id
    try {
      const { data: sls } = await api.get("/shortlists");
      const sl = sls.find(s => (s.candidate_ids||[]).includes(id));
      if (!sl) { toast.error("No shortlist linkage found. Add candidate to shortlist first."); return; }
      const { data } = await api.post("/interviews", {candidate_id:id, client_id:sl.company_id, job_order_id: jobOrderId || sl.job_order_id, interview_type:"First Interview"});
      toast.success("Interview requested"); return data;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const TABS = [
    ["overview","Overview"],["skills","Skills"],["experience","Experience"],
    ["accomplishments","Accomplishments"],["industry","Industry Experience"],
    ["tools","Software & Tools"],["education","Education & Credentials"],
    ["career","Career Profile"],["media","Media & Auditions"],
    ["verification","Verification"],["match","Match Analysis"],
  ];

  return (
    <Layout>
      <div className="mb-4 flex items-center justify-between">
        <button onClick={()=>nav(-1)} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-900" data-testid="emp-back"><ArrowLeft size={12}/> Back to shortlist</button>
        <div className="flex gap-2">
          <button data-testid="emp-favorite" onClick={()=>{setFavorite(!favorite);toast.info(favorite?"Removed from favorites":"Added to favorites");}} className={`h-9 px-3 text-xs rounded border inline-flex items-center gap-1.5 font-semibold ${favorite?"bg-rose-50 border-rose-300 text-rose-700":"border-slate-300 text-slate-600"}`}><Heart size={13} className={favorite?"fill-rose-500 text-rose-500":""}/> Favorite</button>
          <button data-testid="emp-compare" onClick={()=>toast.info("Compare view coming soon in the Comparison workspace")} className="h-9 px-3 text-xs rounded border border-slate-300 inline-flex items-center gap-1.5 font-semibold text-slate-600"><GitCompareArrows size={13}/> Compare</button>
          <button data-testid="emp-request-interview" onClick={requestInterview} className="h-9 px-3 text-xs rounded bg-slate-900 text-white inline-flex items-center gap-1.5 font-semibold"><Calendar size={13}/> Request interview</button>
        </div>
      </div>

      {/* Header snapshot */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-4">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="shrink-0">
            {c.photo_url ? (
              <img src={`${API_BASE.replace(/\/api$/,"")}${c.photo_url}`} alt="" data-testid="emp-photo" className="w-28 h-28 rounded-xl object-cover border border-slate-200"/>
            ) : (
              <div className="w-28 h-28 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400"><User size={32}/></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">{c.first_name} {c.last_name}</h1>
              <TierBadge tier={brandify(c.qualification_tier)}/>
              {c.match && <MatchBadge score={c.match.score} hardFail={c.match.has_hard_fail}/>}
            </div>
            <div className="text-slate-600 mt-1">{sanitizeHeadline(c.headline) || c.primary_role}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mt-4 text-sm">
              <Snap k="Target role" v={c.primary_role}/>
              <Snap k="Seniority" v={c.seniority}/>
              <Snap k="Location" v={[c.city,c.country].filter(Boolean).join(", ")}/>
              <Snap k="Time zone" v={c.time_zone || c.utc_offset}/>
              <Snap k="Years experience" v={c.years_experience}/>
              <Snap k="Availability" v={c.earliest_start}/>
              <Snap k="Work arrangement" v={(c.work_arrangement||[]).join(", ") || c.remote_type}/>
              <Snap k="Compensation" v={c.desired_compensation ? `$${c.desired_compensation.toLocaleString()}/mo` : "—"}/>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-slate-200 mb-5 flex gap-1 overflow-x-auto">
        {TABS.map(([id2,lbl])=>(
          <button key={id2} data-testid={`emp-tab-${id2}`} onClick={()=>setTab(id2)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${tab===id2?"border-sky-600 text-slate-900":"border-transparent text-slate-500 hover:text-slate-800"}`}>{lbl}</button>
        ))}
      </div>

      {tab==="overview" && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card title="Professional summary" className="md:col-span-2"><p className="text-sm text-slate-700 whitespace-pre-line">{c.summary || <Empty text="No summary provided."/>}</p></Card>
          <Card title="Highlights">
            <ul className="text-sm text-slate-700 space-y-1.5">
              {c.qualification_tier==="StaffHub Premier" && <li className="flex items-start gap-1.5"><Star size={13} className="text-amber-500 mt-1"/> ConnectMyHub Premier — top-tier vetting</li>}
              {c.qualification_tier==="StaffHub Qualified" && <li className="flex items-start gap-1.5"><ShieldCheck size={13} className="text-emerald-600 mt-1"/> ConnectMyHub Qualified</li>}
              {c.staffhub_communication_score>=80 && <li className="flex items-start gap-1.5"><MessageSquare size={13} className="text-sky-600 mt-1"/> Communication assessed ≥ 80/100</li>}
              {hasIntro && <li className="flex items-start gap-1.5"><Video size={13} className="text-sky-600 mt-1"/> Video introduction available</li>}
              {auditions.length>0 && <li className="flex items-start gap-1.5"><Mic size={13} className="text-amber-600 mt-1"/> {auditions.length} role audition{auditions.length>1?"s":""} submitted</li>}
              {(c.years_experience||0)>=5 && <li className="flex items-start gap-1.5"><CheckCircle2 size={13} className="text-emerald-600 mt-1"/> {c.years_experience}+ years experience</li>}
            </ul>
          </Card>
        </div>
      )}

      {tab==="skills" && (
        <div className="space-y-4">
          <Card title="Structured skills">
            <StructuredSkills c={c}/>
          </Card>
          {(c.languages_detailed||[]).length>0 && (
            <Card title="Languages">
              <table className="w-full text-sm"><tbody>{c.languages_detailed.map((l,i)=>(
                <tr key={i} className="border-t border-slate-100 first:border-none"><td className="py-2 font-semibold w-40">{l.language}</td><td className="py-2 text-slate-600">Spoken: {l.spoken_proficiency||"—"}</td><td className="py-2 text-slate-600">Written: {l.written_proficiency||"—"}</td><td className="py-2 text-slate-500">{l.primary?"Primary":""}</td></tr>
              ))}</tbody></table>
            </Card>
          )}
        </div>
      )}

      {tab==="experience" && (
        <Card title="Relevant experience">
          {(c.employment_history||[]).length===0 ? <Empty text="No employment history."/> :
            <div className="space-y-3">{c.employment_history.map((e,i)=>(
              <div key={i} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div><div className="font-semibold text-slate-900">{e.job_title} — {e.employer}</div>
                    <div className="text-xs text-slate-500">{e.industry} · {e.location} · {e.start_date}{e.current?" – Present":e.end_date?` – ${e.end_date}`:""}</div></div>
                  <EvidenceBadge level={e.verification_status || "CANDIDATE_REPORTED"}/>
                </div>
                {e.responsibilities && <p className="mt-2 text-sm text-slate-700">{e.responsibilities}</p>}
                {e.accomplishments && <div className="mt-2 text-sm text-slate-700"><span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">Accomplishments</span><p>{e.accomplishments}</p></div>}
              </div>
            ))}</div>}
        </Card>
      )}

      {tab==="accomplishments" && (
        <Card title="Measurable accomplishments">
          {(c.employment_history||[]).filter(e=>e.quantifiable_results || e.accomplishments).length===0 ? <Empty text="No documented accomplishments yet."/> :
            <ul className="space-y-3">{c.employment_history.filter(e=>e.quantifiable_results||e.accomplishments).map((e,i)=>(
              <li key={i} className="border border-slate-200 rounded p-3">
                <div className="text-xs text-slate-500 mb-1">{e.job_title} at {e.employer}</div>
                {e.quantifiable_results && <div className="text-sm text-emerald-800 font-medium">{e.quantifiable_results}</div>}
                {e.accomplishments && <div className="text-sm text-slate-700 mt-1">{e.accomplishments}</div>}
                <div className="mt-2"><EvidenceBadge level={e.verification_status || "CANDIDATE_REPORTED"}/></div>
              </li>
            ))}</ul>}
        </Card>
      )}

      {tab==="industry" && (
        <Card title="Industry experience"><Tags items={c.industries}/></Card>
      )}

      {tab==="tools" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Software"><Tags items={c.software_skills}/></Card>
          <Card title="CRM"><Tags items={c.crm_experience}/></Card>
          <Card title="AI tools"><Tags items={c.ai_tools}/></Card>
          <Card title="Industry systems"><Tags items={c.industry_systems}/></Card>
        </div>
      )}

      {tab==="education" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Education">
            {(c.education_history||[]).length===0 ? <Empty text="No education records."/> :
              <div className="space-y-2">{c.education_history.map((e,i)=>(
                <div key={i} className="border border-slate-200 rounded p-3"><div className="font-semibold text-sm text-slate-900">{e.degree || e.degree_level} · {e.field}</div><div className="text-xs text-slate-500">{e.institution} · {e.graduation_date}</div></div>
              ))}</div>}
          </Card>
          <Card title="Certifications & Credentials">
            {(c.certifications_detailed||[]).length===0 && (c.certifications||[]).length===0 ? <Empty text="No certifications."/> :
              <div className="space-y-2">{(c.certifications_detailed||[]).map((cx,i)=>(
                <div key={i} className="border border-slate-200 rounded p-3 text-xs"><div className="font-semibold text-slate-900 text-sm">{cx.name}</div><div className="text-slate-500">{cx.issuing_organization} · issued {cx.issue_date}{cx.expiration_date?` · expires ${cx.expiration_date}`:""}</div></div>
              ))}
              {(c.certifications||[]).length>0 && <Tags items={c.certifications}/>}
              </div>}
          </Card>
        </div>
      )}

      {tab==="career" && (
        <Card title="Career profile">
          {c.career_profile && Object.keys(c.career_profile).some(k=>c.career_profile[k]) ? (
            <div className="space-y-4">{Object.entries(c.career_profile).map(([k,v])=>v && (
              <div key={k}><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">{k.replace(/_/g," ")}</div><p className="text-sm text-slate-800 whitespace-pre-line">{v}</p></div>
            ))}</div>
          ) : <Empty text="Career profile not shared."/>}
        </Card>
      )}

      {tab==="media" && (
        <div className="space-y-4">
          {hasIntro && <MediaSection title="Video Introduction" items={groupedMedia.VIDEO_INTRODUCTION} playing={playing} setPlaying={setPlaying}/>}
          {interviewResponses.length>0 && <MediaSection title="Interview Responses" items={interviewResponses} playing={playing} setPlaying={setPlaying} showQuestion/>}
          {auditions.length>0 && <MediaSection title="Role Auditions" items={auditions} playing={playing} setPlaying={setPlaying}/>}
          {workSamples.length>0 && <MediaSection title="Work Samples" items={workSamples} playing={playing} setPlaying={setPlaying}/>}
          {media.length===0 && <Card title="Media & Auditions"><Empty text="No approved media yet. Your recruiter will notify you when this candidate's media is available."/></Card>}
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={11}/> Media streams only when you click Play — nothing is preloaded.</div>
        </div>
      )}

      {tab==="verification" && (
        <Card title="Trust & Verification Summary" icon={ShieldCheck}>
          <p className="text-xs text-slate-500 mb-4">Only <span className="font-semibold text-emerald-700">VERIFIED</span> means ConnectMyHub actually verified this area.</p>
          <div className="grid md:grid-cols-2 gap-2">
            {(c.verification_summary||[]).map((it,i)=>(
              <div key={i} className="flex items-center justify-between border border-slate-200 rounded p-3">
                <div><div className="text-sm font-medium text-slate-800">{it.area}</div>{it.evidence_level && <div className="mt-1"><EvidenceBadge level={it.evidence_level}/></div>}</div>
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${STATUS_TONE[it.status]||STATUS_TONE.PENDING}`} data-testid={`emp-verify-${it.area.replace(/\s+/g,'-').toLowerCase()}`}>{it.status.replace("_"," ")}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab==="match" && (
        <Card title="Match analysis" icon={Star}>
          {!c.match ? <Empty text="Match analysis is available when viewing this candidate against a specific job order."/> :
            <div className="space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Match score</div><div className={`font-display text-4xl font-bold ${c.match.has_hard_fail?"text-red-600":c.match.score>=80?"text-emerald-600":c.match.score>=60?"text-amber-600":"text-slate-500"}`}>{c.match.score}%</div></div>
                {c.match.has_hard_fail && <div className="text-[10px] uppercase tracking-widest font-semibold text-red-600 flex items-center gap-1"><AlertTriangle size={12}/> Hard requirement fail</div>}
              </div>
              {c.match.has_hard_fail && (c.match.hard_fails||[]).length>0 && (
                <div className="p-3 rounded bg-red-50 border border-red-200"><div className="text-[11px] font-semibold text-red-800 uppercase tracking-widest mb-1.5">Hard requirement failures</div><ul className="text-xs text-red-900 space-y-0.5">{c.match.hard_fails.map((s,i)=><li key={i}>· {s}</li>)}</ul></div>
              )}
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3"><div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-widest mb-2">Why ConnectMyHub recommends this candidate</div><ul className="text-sm text-emerald-900 space-y-1">{(c.match.strengths||[]).map((s,i)=><li key={i}>· {s}</li>)}</ul></div>
                <div className="bg-amber-50 border border-amber-200 rounded p-3"><div className="text-[11px] font-semibold text-amber-800 uppercase tracking-widest mb-2">Legitimate gaps / concerns</div><ul className="text-sm text-amber-900 space-y-1">{(c.match.concerns||[]).length===0?<li>· None flagged</li>:c.match.concerns.map((s,i)=><li key={i}>· {s}</li>)}</ul></div>
              </div>
            </div>}
        </Card>
      )}
    </Layout>
  );
}

const Card = ({title, icon:Ic, children, className=""}) => (
  <section className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${className}`}>
    <div className="flex items-center gap-2 mb-3">{Ic && <Ic size={14} className="text-sky-600"/>}<h3 className="text-sm font-semibold text-slate-900">{title}</h3></div>
    {children}
  </section>
);
const Snap = ({k,v}) => (<div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</div><div className="text-slate-900 text-sm font-medium">{v || "—"}</div></div>);
const Empty = ({text}) => <div className="text-sm text-slate-500 italic">{text}</div>;
const Tags = ({items}) => (items||[]).length===0 ? <Empty text="None on file."/> : <div className="flex flex-wrap gap-1.5">{items.map((s,i)=><span key={i} className="text-xs px-2.5 py-1 rounded border border-slate-200 bg-slate-50 text-slate-700">{s}</span>)}</div>;
const EvidenceBadge = ({level}) => (<span className={`inline-flex text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${EVIDENCE_TONE[level] || EVIDENCE_TONE.CANDIDATE_REPORTED}`}>{EVIDENCE_LABEL[level] || level}</span>);
const TierBadge = ({tier}) => {
  const tone = tier==="StaffHub Premier"?"bg-amber-50 text-amber-800 border-amber-300":tier==="StaffHub Qualified"?"bg-emerald-50 text-emerald-700 border-emerald-200":"bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded border ${tone}`}><ShieldCheck size={11}/> {tier || "Applicant"}</span>;
};
const MatchBadge = ({score, hardFail}) => (
  <span data-testid="emp-match-badge" className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded border ${hardFail?"bg-red-50 border-red-300 text-red-700":score>=80?"bg-emerald-50 border-emerald-300 text-emerald-800":score>=60?"bg-amber-50 border-amber-300 text-amber-800":"bg-slate-100 border-slate-200 text-slate-700"}`}><Star size={11}/> Match {score}%{hardFail?" (hard req fail)":""}</span>
);

function MediaSection({title, items, playing, setPlaying, showQuestion}) {
  return (
    <Card title={title}>
      <div className="space-y-3">{items.map(m=>{
        const isVideo = m.media_type.includes("VIDEO") || m.media_type==="VIDEO_INTRODUCTION";
        const active = playing===m.id;
        return (
          <div key={m.id} className="border border-slate-200 rounded-lg p-3" data-testid={`emp-media-${m.id}`}>
            {showQuestion && m.question_prompt && (
              <div className="mb-2"><div className="text-[10px] uppercase tracking-widest font-semibold text-sky-700 mb-0.5">Question</div><div className="text-sm text-slate-900 font-medium">{m.question_prompt}</div></div>
            )}
            {!showQuestion && m.title && <div className="text-sm text-slate-900 font-medium mb-1">{m.title}</div>}
            <button onClick={()=>setPlaying(active?null:m.id)} data-testid={`emp-play-${m.id}`} className="h-9 px-3 text-xs rounded border border-slate-300 inline-flex items-center gap-1.5 font-semibold hover:bg-slate-50"><Play size={12}/> {MEDIA_KIND[m.media_type]?.label || "Play"}</button>
            {active && (
              <div className="mt-3">
                {isVideo ? (
                  <video controls autoPlay preload="none" className="w-full max-w-lg rounded border border-slate-200" src={`${API_BASE}/media/${m.id}/stream`} crossOrigin="use-credentials" data-testid={`emp-player-${m.id}`}/>
                ) : (
                  <audio controls autoPlay preload="none" className="w-full max-w-lg" src={`${API_BASE}/media/${m.id}/stream`} crossOrigin="use-credentials" data-testid={`emp-player-${m.id}`}/>
                )}
              </div>
            )}
          </div>
        );
      })}</div>
    </Card>
  );
}

function StructuredSkills({c}) {
  const structured = c.skills_structured || null;
  if (structured && Array.isArray(structured) && structured.length>0) {
    return (
      <table className="w-full text-sm"><thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left px-3 py-2">Skill</th><th className="text-left px-3 py-2">Proficiency</th><th className="text-left px-3 py-2">Years</th><th className="text-left px-3 py-2">Evidence</th></tr></thead>
        <tbody>{structured.map((s,i)=>(
          <tr key={i} className="border-t border-slate-100"><td className="px-3 py-2 font-semibold">{s.skill || s.name}</td><td className="px-3 py-2">{s.proficiency}</td><td className="px-3 py-2 font-mono">{s.years_experience || "—"}</td><td className="px-3 py-2"><EvidenceBadge level={s.evidence_level || "CANDIDATE_REPORTED"}/></td></tr>
        ))}</tbody></table>
    );
  }
  return (
    <div className="space-y-3">
      <div><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Professional</div><Tags items={c.skills}/></div>
      {(c.software_skills||[]).length>0 && <div><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Software / Tools</div><Tags items={c.software_skills}/></div>}
    </div>
  );
}
